import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaMapMarkerAlt,
  FaTrash,
  FaHeart,
  FaPlus,
  FaMinus,
  FaInfoCircle,
} from 'react-icons/fa';
import { useCart } from '../contexts/CartContext';
import { checkoutService } from '../services/apiService';
import { formatPrice } from '../utils/currency';
import { isCodEligible, saveItemForLater } from '../utils/checkoutUtils';
import { CartSkeleton } from './Skeleton';
import { cartService } from '../services/apiService';
import MarketplaceCheckout from './MarketplaceCheckout';

const PAYMENT_OPTIONS = [
  { id: 'upi', label: 'UPI' },
  { id: 'card', label: 'Card' },
  { id: 'netbanking', label: 'Net Banking' },
  { id: 'emi', label: 'EMI' },
  { id: 'cod', label: 'Cash on Delivery' },
];

function AssuranceTooltip() {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex items-center gap-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-slate-400 hover:text-amber-300"
        aria-label="Platform fees info"
      >
        <FaInfoCircle />
      </button>
      {open && (
        <span className="absolute bottom-full left-0 z-20 mb-2 w-56 rounded-lg border border-slate-600 bg-slate-900 p-2 text-xs text-slate-200 shadow-xl">
          Platform service fee for order processing and support.
        </span>
      )}
    </span>
  );
}

/**
 * B-07 — Cart & Checkout (unified)
 */
function CartCheckoutContent() {
  const navigate = useNavigate();
  const { updateQuantity, refreshCart, applyCoupon, removeCoupon, appliedCoupon } = useCart();

  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(null);
  const [quote, setQuote] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [codConfig, setCodConfig] = useState({ pincodes: [], maxAmount: 2000 });
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [priceConfirmed, setPriceConfirmed] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [pendingCheckout, setPendingCheckout] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponBusy, setCouponBusy] = useState(false);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [newAddress, setNewAddress] = useState({
    label: 'Home',
    name: '',
    mobile: '',
    fullAddress: '',
    city: '',
    state: '',
    pincode: '',
  });

  const loadAll = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [cartRes, addrRes, codRes] = await Promise.all([
        checkoutService.getEnrichedCart(),
        checkoutService.getAddresses(),
        checkoutService.getCodPincodes(),
      ]);
      const c = cartRes?.cart || cartRes;
      setCart(c);
      setAddresses(addrRes);
      setCodConfig(codRes);
      const defaultAddr = addrRes.find((a) => a.isDefault) || addrRes[0];
      if (defaultAddr) {
        setSelectedAddressId((prev) => prev || defaultAddr._id);
      }
      if (!c?.items?.length) {
        try {
          const pending = await checkoutService.getPendingCheckout();
          setPendingCheckout(pending || null);
        } catch {
          setPendingCheckout(null);
        }
      } else {
        setPendingCheckout(null);
      }
    } catch (err) {
      if (!silent) toast.error(err.message || 'Failed to load checkout');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
    refreshCart?.();
  }, [loadAll, refreshCart]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        loadAll({ silent: true });
        refreshCart?.();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadAll, refreshCart]);

  useEffect(() => {
    if (!selectedAddressId && !cart?.items?.length) return;
    (async () => {
      try {
        const q = await checkoutService.getQuote({ addressId: selectedAddressId });
        setQuote(q);
      } catch (_) {}
    })();
  }, [selectedAddressId, cart?.items?.length, priceConfirmed, appliedCoupon?.code, appliedCoupon?.discountAmount]);

  const handleApplyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) {
      toast.error('Enter a coupon code');
      return;
    }
    setCouponBusy(true);
    try {
      await applyCoupon(code, selectedPayment);
      setCouponCode('');
      await loadAll({ silent: true });
      toast.success('Coupon applied');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Invalid coupon');
    } finally {
      setCouponBusy(false);
    }
  };

  const handleRemoveCoupon = async () => {
    setCouponBusy(true);
    try {
      await removeCoupon();
      await loadAll({ silent: true });
      toast.success('Coupon removed');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not remove coupon');
    } finally {
      setCouponBusy(false);
    }
  };

  const activeCoupon = appliedCoupon || cart?.appliedCoupon || quote?.appliedCoupon;

  const selectedAddress = useMemo(
    () => addresses.find((a) => String(a._id) === String(selectedAddressId)),
    [addresses, selectedAddressId]
  );

  const codAllowed = useMemo(() => {
    const total = quote?.breakdown?.grandTotal ?? 0;
    return isCodEligible(total, selectedAddress?.pincode, codConfig.pincodes || []);
  }, [quote, selectedAddress, codConfig]);

  const paymentOptions = useMemo(() => {
    const total = quote?.breakdown?.grandTotal ?? 0;
    return PAYMENT_OPTIONS.filter((opt) => {
      if (opt.id === 'cod') return codAllowed;
      if (opt.id === 'emi') return total > 3000;
      return true;
    });
  }, [codAllowed, quote]);

  const hasOutOfStock = cart?.hasOutOfStock;
  const hasPriceChanges = cart?.hasPriceChanges && !priceConfirmed;

  const canPlaceOrder =
    !hasOutOfStock &&
    !hasPriceChanges &&
    selectedAddressId &&
    selectedPayment &&
    (cart?.items?.length || 0) > 0;

  const handleRemove = async (item) => {
    try {
      await checkoutService.removeCartItem(item.productId);
      await refreshCart?.();
      await loadAll();
      toast.success('Item removed');
    } catch {
      await cartService.removeItem(item.productId, item.partNumber, item.dealId);
      await loadAll();
    }
  };

  const handleSaveForLater = async (item) => {
    saveItemForLater(item);
    await handleRemove(item);
    toast.success('Saved for later in wishlist');
  };

  const handleQty = async (item, qty) => {
    if (qty < 1) return;
    await updateQuantity(item.productId, qty, item.partNumber, item.dealId);
    await loadAll();
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    try {
      const addr = await checkoutService.addAddress(newAddress);
      setAddresses((prev) => [...prev, addr]);
      setSelectedAddressId(addr._id);
      setShowAddAddress(false);
      toast.success('Address saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save address');
    }
  };

  const handleRestorePendingCart = async () => {
    setRestoring(true);
    try {
      await checkoutService.restorePendingCart();
      await refreshCart?.();
      await loadAll();
      toast.success('Your cart has been restored.');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not restore cart');
    } finally {
      setRestoring(false);
    }
  };

  const handleResumePayment = () => {
    if (!pendingCheckout) return;
    localStorage.setItem('pendingRazorpayCheckout', JSON.stringify(pendingCheckout));
    navigate('/checkout/payment', { state: { razorpayCheckout: pendingCheckout } });
  };

  const handlePlaceOrder = async () => {
    if (!canPlaceOrder) return;
    setPlacing(true);
    try {
      const res = await checkoutService.createCheckoutOrder({
        addressId: selectedAddressId,
        paymentMethod: selectedPayment,
        priceConfirmed: priceConfirmed || !cart?.hasPriceChanges,
        cartItems: (cart?.items || []).map((i) => ({
          listingId: i.productId,
          quantity: i.quantity,
        })),
      });

      if (res.razorpayOrderId) {
        localStorage.setItem(
          'pendingRazorpayCheckout',
          JSON.stringify({
            orderId: res.orderId,
            razorpayOrderId: res.razorpayOrderId,
            amount: res.amount,
            currency: res.currency,
          })
        );
        toast.success('Order created — proceed to payment');
        navigate('/checkout/payment', { state: { razorpayCheckout: res } });
      } else {
        await refreshCart?.();
        toast.success('Order placed successfully');
        navigate('/checkout/confirmation', { state: { order: { orderId: res.orderId } } });
      }
    } catch (err) {
      if (err.code === 'PRICE_CHANGED') {
        setPriceConfirmed(false);
        await loadAll();
        toast.warning('Prices changed — please review and confirm.');
      } else if (err.code === 'OUT_OF_STOCK') {
        await loadAll();
        toast.error('Some items are out of stock. Remove them to continue.');
      } else if (err.code === 'COD_INELIGIBLE') {
        toast.error('COD is not available. Choose another payment method.');
        setSelectedPayment(null);
      } else {
        toast.error(err.message || 'Could not place order');
      }
    } finally {
      setPlacing(false);
    }
  };

  if (loading) return <CartSkeleton />;

  if (!cart?.items?.length) {
    return (
      <div className="min-h-screen bg-[#0b1120] px-4 py-16 text-center text-slate-100">
        <p className="text-lg font-semibold">Your cart is empty</p>
        {pendingCheckout && (
          <div className="mx-auto mt-6 max-w-md rounded-2xl border border-amber-700/40 bg-amber-950/20 p-5 text-left">
            <p className="text-sm text-amber-200">
              You have an unpaid order ({pendingCheckout.itemCount || 1} item
              {pendingCheckout.itemCount === 1 ? '' : 's'}). Your cart was cleared when checkout
              started — restore it or resume payment.
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={handleRestorePendingCart}
                disabled={restoring}
                className="flex-1 rounded-xl bg-amber-500 py-2.5 text-sm font-bold text-slate-900 hover:bg-amber-400 disabled:opacity-50"
              >
                {restoring ? 'Restoring…' : 'Restore cart'}
              </button>
              {pendingCheckout.razorpayOrderId && (
                <button
                  type="button"
                  onClick={handleResumePayment}
                  className="flex-1 rounded-xl border border-amber-500 py-2.5 text-sm font-bold text-amber-400 hover:bg-amber-500/10"
                >
                  Resume payment
                </button>
              )}
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={() => navigate('/catalog?spareType=used')}
          className="mt-6 rounded-xl bg-amber-500 px-6 py-2.5 font-bold text-slate-900"
        >
          Browse parts
        </button>
      </div>
    );
  }

  const breakdown = quote?.breakdown;

  return (
    <div className="min-h-screen bg-[#0b1120] pb-28 text-slate-100">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="pl-12 sm:pl-14">
          <h1 className="text-2xl font-bold text-white sm:text-3xl">Cart & Checkout</h1>
          <p className="mt-1 text-sm text-slate-400">Review items, choose delivery, and pay</p>
        </div>

        {hasOutOfStock && (
          <div className="mt-4 rounded-xl border border-red-700/50 bg-red-950/30 px-4 py-3 text-sm text-red-200">
            Remove out-of-stock items before checkout.
          </div>
        )}

        {cart?.hasPriceChanges && !priceConfirmed && (
          <div className="mt-4 rounded-xl border border-amber-700/50 bg-amber-950/30 px-4 py-3 text-sm text-amber-200">
            <p className="font-semibold">Prices have changed since you added these items.</p>
            <ul className="mt-2 space-y-1">
              {(cart.priceChangedItems || []).map((item) => (
                <li key={item.productId}>
                  {item.name}: {formatPrice(item.snapshotPrice)} →{' '}
                  {formatPrice(item.currentUnitPrice)}
                  {item.priceDelta > 0 ? ' (↑)' : ' (↓)'}
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => setPriceConfirmed(true)}
              className="mt-3 rounded-lg bg-amber-500 px-4 py-2 text-xs font-bold text-slate-900"
            >
              Confirm updated prices
            </button>
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="space-y-6 lg:col-span-7">
            {(cart.sellerGroups || []).map((group) => (
              <section
                key={group.sellerCity}
                className="overflow-hidden rounded-2xl border border-slate-700/60 bg-slate-800/40"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-700/60 bg-slate-900/50 px-4 py-3">
                  <p className="flex items-center gap-2 text-sm font-semibold text-white">
                    <FaMapMarkerAlt className="text-amber-400" />
                    Seller — {group.sellerCity}
                  </p>
                  <span className="text-xs text-slate-400">
                    Estimated: {group.etaLabel}
                  </span>
                </div>

                <div className="divide-y divide-slate-700/40">
                  {group.items.map((item) => (
                    <div
                      key={item.itemKey || item.productId}
                      className={`flex gap-4 p-4 ${item.outOfStock ? 'opacity-50' : ''}`}
                    >
                      <img
                        src={item.imageUrl || 'https://via.placeholder.com/80'}
                        alt=""
                        className="h-20 w-20 shrink-0 rounded-lg object-cover bg-slate-900"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium text-white">{item.name}</p>
                          {item.gradeLabel && (
                            <span className="rounded-full border border-amber-600/40 bg-amber-950/40 px-2 py-0.5 text-[10px] font-bold text-amber-300">
                              {item.gradeLabel}
                            </span>
                          )}
                          {item.outOfStock && (
                            <span className="text-xs font-semibold text-red-400">Out of stock</span>
                          )}
                          {item.priceChanged && (
                            <span className="text-xs font-semibold text-amber-400">Price updated</span>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-slate-400">{item.brand}</p>
                        <p className="mt-2 font-bold text-amber-400">
                          {formatPrice(item.currentUnitPrice ?? item.discountPrice ?? item.price)}
                        </p>

                        {item.isSpecProduct && !item.outOfStock && (
                          <div className="mt-2 flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleQty(item, item.quantity - 1)}
                              className="rounded border border-slate-600 p-1"
                            >
                              <FaMinus className="text-xs" />
                            </button>
                            <span className="text-sm">{item.quantity}</span>
                            <button
                              type="button"
                              onClick={() => handleQty(item, item.quantity + 1)}
                              className="rounded border border-slate-600 p-1"
                            >
                              <FaPlus className="text-xs" />
                            </button>
                          </div>
                        )}

                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handleRemove(item)}
                            className="text-xs font-semibold text-red-400 hover:text-red-300"
                          >
                            <FaTrash className="mr-1 inline" /> Remove
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSaveForLater(item)}
                            className="text-xs font-semibold text-slate-400 hover:text-slate-200"
                          >
                            <FaHeart className="mr-1 inline" /> Save for later
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}

            <section className="rounded-2xl border border-slate-700/60 bg-slate-800/40 p-4 sm:p-5">
              <h2 className="text-sm font-bold uppercase tracking-wide text-slate-400">
                Delivery address
              </h2>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {addresses.map((addr) => (
                  <button
                    key={addr._id}
                    type="button"
                    onClick={() => setSelectedAddressId(addr._id)}
                    className={`rounded-xl border p-4 text-left text-sm transition-colors ${
                      String(selectedAddressId) === String(addr._id)
                        ? 'border-amber-500 bg-amber-950/20'
                        : 'border-slate-600 bg-slate-900/40 hover:border-slate-500'
                    }`}
                  >
                    <p className="font-semibold text-white">{addr.label || 'Address'}</p>
                    <p className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-slate-300">
                      <span className="break-words">{addr.name}</span>
                      <span className="whitespace-nowrap">{addr.mobile}</span>
                    </p>
                    <p className="mt-1 break-words text-slate-400">{addr.fullAddress}</p>
                    <p className="mt-1 text-slate-500">{addr.pincode}</p>
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setShowAddAddress((v) => !v)}
                className="mt-4 text-sm font-semibold text-amber-400 hover:text-amber-300"
              >
                + Add new address
              </button>
              {showAddAddress && (
                <form onSubmit={handleAddAddress} className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {['name', 'mobile', 'fullAddress', 'city', 'state', 'pincode'].map((field) => (
                    <input
                      key={field}
                      required={['name', 'mobile', 'fullAddress', 'pincode'].includes(field)}
                      placeholder={field}
                      value={newAddress[field]}
                      onChange={(e) => setNewAddress({ ...newAddress, [field]: e.target.value })}
                      className="rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-white"
                    />
                  ))}
                  <button
                    type="submit"
                    className="sm:col-span-2 rounded-xl bg-amber-500 py-2.5 font-bold text-slate-900"
                  >
                    Save address
                  </button>
                </form>
              )}
            </section>
          </div>

          <div className="lg:col-span-5">
            <div className="sticky top-24 space-y-5 rounded-2xl border border-slate-700/60 bg-slate-800/50 p-5">
              <h2 className="text-sm font-bold uppercase tracking-wide text-slate-400">
                Order summary
              </h2>

              <div className="rounded-xl border border-slate-600/60 bg-slate-900/40 p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Coupon
                </p>
                {activeCoupon?.code ? (
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-emerald-300">
                      {activeCoupon.code} applied
                      {breakdown?.couponDiscount > 0
                        ? ` (−${formatPrice(breakdown.couponDiscount)})`
                        : ''}
                    </p>
                    <button
                      type="button"
                      onClick={handleRemoveCoupon}
                      disabled={couponBusy}
                      className="rounded-lg border border-slate-600 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
                    <input
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Enter coupon code"
                      className="min-w-0 w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-white placeholder:text-slate-500"
                    />
                    <button
                      type="button"
                      onClick={handleApplyCoupon}
                      disabled={couponBusy}
                      className="rounded-lg bg-amber-500 px-3 py-2 text-sm font-bold text-slate-900 hover:bg-amber-400 disabled:opacity-50"
                    >
                      Apply
                    </button>
                  </div>
                )}
              </div>

              {breakdown && (
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between text-slate-300">
                    <dt>Parts subtotal</dt>
                    <dd>{formatPrice(breakdown.partsSubtotal)}</dd>
                  </div>
                  {(breakdown.gstLines || []).map((line) => (
                    <div key={line.rate} className="flex justify-between text-slate-400">
                      <dt>GST ({line.rate}%)</dt>
                      <dd>{formatPrice(line.gstAmount)}</dd>
                    </div>
                  ))}
                  <div className="flex justify-between text-slate-300">
                    <dt className="flex items-center gap-1">
                      Platform fees <AssuranceTooltip />
                    </dt>
                    <dd>{formatPrice(breakdown.assuranceLevy)}</dd>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <dt>Delivery</dt>
                    <dd>{breakdown.deliveryCharge === 0 ? 'Free' : formatPrice(breakdown.deliveryCharge)}</dd>
                  </div>
                  {breakdown.walletCreditApplied > 0 && (
                    <div className="flex justify-between text-emerald-300">
                      <dt>Wallet credit</dt>
                      <dd>-{formatPrice(breakdown.walletCreditApplied)}</dd>
                    </div>
                  )}
                  {breakdown.couponDiscount > 0 && (
                    <div className="flex justify-between text-emerald-300">
                      <dt>Coupon discount</dt>
                      <dd>-{formatPrice(breakdown.couponDiscount)}</dd>
                    </div>
                  )}
                  <div className="border-t border-slate-600 pt-3 flex justify-between text-lg font-bold text-amber-400">
                    <dt>Grand Total</dt>
                    <dd>{formatPrice(breakdown.grandTotal)}</dd>
                  </div>
                </dl>
              )}

              <div>
                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  Payment method
                </h3>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {paymentOptions.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setSelectedPayment(opt.id)}
                      className={`rounded-xl border px-3 py-2.5 text-sm font-semibold ${
                        selectedPayment === opt.id
                          ? 'border-amber-500 bg-amber-950/30 text-amber-300'
                          : 'border-slate-600 text-slate-300 hover:border-slate-500'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {!codAllowed && (
                  <p className="mt-2 text-xs text-slate-500">
                    COD unavailable — order must be under ₹2,000 and pincode must be eligible.
                  </p>
                )}
              </div>

              <button
                type="button"
                disabled={!canPlaceOrder || placing}
                onClick={handlePlaceOrder}
                className="w-full rounded-xl bg-amber-500 py-4 text-base font-bold text-slate-900 hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {placing ? 'Placing order…' : 'Place Order'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CartCheckout() {
  const [searchParams] = useSearchParams();
  const catalogNodeId = searchParams.get('catalogNodeId');
  const listingId = searchParams.get('listingId');
  const marketplaceVehicleId = searchParams.get('vehicleId');

  if (catalogNodeId && listingId) {
    return (
      <MarketplaceCheckout
        catalogNodeId={catalogNodeId}
        listingId={listingId}
        vehicleId={marketplaceVehicleId}
      />
    );
  }

  return <CartCheckoutContent />;
}
